import { ErrorMessageSection } from "./shared";

export function AlternativesContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Why Consider Sugargoo Alternatives?</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          While Sugargoo is an excellent shopping agent for most users, exploring alternatives helps you understand the competitive landscape and potentially find a better fit for your specific shopping priorities — whether that means lower fees, faster processing, different shipping partnerships, or a preferred user interface. Every agent has unique strengths, and experienced international shoppers often maintain accounts with multiple platforms.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          This comparison draws on community feedback, platform feature analysis, and real user experiences cataloged across various <strong>Sugargoo Spreadsheet</strong> resources and shopping communities. Use it as a starting point for your own research rather than a definitive ranking.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Top Shopping Agents Compared</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-background-300">
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Agent</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Best For</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Fee Level</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">UI Quality</th>
              </tr>
            </thead>
            <tbody>
              {[
                { agent: "Sugargoo", best: "All-around reliable shopping with great QC", fee: "Competitive", ui: "Clean & intuitive" },
                { agent: "Pandabuy", best: "Modern interface, strong community features", fee: "Competitive", ui: "Excellent" },
                { agent: "CSSBuy", best: "Budget-conscious shoppers, low fees", fee: "Low", ui: "Functional" },
                { agent: "Superbuy", best: "Long-established, extensive experience", fee: "Moderate", ui: "Good" },
                { agent: "WeGoBuy", best: "Simple interface, beginner-friendly", fee: "Moderate", ui: "Simple" },
              ].map((row) => (
                <tr key={row.agent} className="border-b border-background-200/50">
                  <td className="py-3 px-4 font-medium text-foreground-800">{row.agent}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.best}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.fee}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.ui}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How to Choose the Right Agent for You</h2>
        <div className="space-y-5">
          {[
            { title: "Prioritize What Matters Most", desc: "Is it absolute lowest fees? Fastest processing? Best mobile app? Most active community? List your top three priorities and compare agents against those criteria rather than trying to find a 'perfect' agent." },
            { title: "Test with Small Orders", desc: "Create accounts on 2-3 platforms and place small test orders. First-hand experience with each interface, QC quality, and shipping process reveals more than any comparison table." },
            { title: "Check Current Promotions", desc: "Agent promotions change regularly. An agent with slightly higher base fees might offer better overall value when current shipping discounts and welcome coupons are factored in." },
            { title: "Consider Your Location", desc: "Shipping costs and available carriers vary by destination country. An agent that is cheapest for US shoppers might not be cheapest for European or Australian delivery addresses." },
          ].map((item, i) => (
            <div key={i} className="flex gap-4">
              <span className="text-xl font-heading font-bold text-primary-300 flex-shrink-0 leading-none">0{i + 1}</span>
              <div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-800 mb-1">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ErrorMessageSection />
    </>
  );
}